import { useState } from "react";
import { ChevronRight, X } from "lucide-react";
import { Button } from "~/components/ui/button";
import { useViewMode } from "~/hooks/useViewMode";
import { ResizableSidePanel } from "~/pages/manifold-nextgen/components/ResizableSidePanel";
import { TabHeader } from "~/pages/manifold-nextgen/components/TabHeader";
import { ViewToggle } from "~/pages/manifold-nextgen/components/ViewToggle";
import { targetStep } from "~/services/pinnedArtifacts";
import { PinnedArtifactWorkflow } from "./PinnedArtifactWorkflow";
import { PinnedArtifacts } from "./PinnedArtifacts";
import { usePinnedArtifactMutations, usePinnedArtifacts } from "./usePinnedArtifacts";
import { useProjectContext } from "./useProjectContext";

/** The project's shared artifacts, as their own page, with the selected one's
 *  lineage in a side panel. Every member sees them; owners curate. */
export const ProjectArtifacts = () => {
  const { project, isOwner, sessions } = useProjectContext();
  const [view, setView] = useViewMode("nextgen-project-artifacts-view");
  const [selectedPinId, setSelectedPinId] = useState<string | null>(null);
  const { pinnedArtifacts } = usePinnedArtifacts(project.id);
  const { unpin } = usePinnedArtifactMutations(project.id);
  // Derived rather than stored, so unsharing the open artifact closes its panel.
  const selected = pinnedArtifacts?.find((p) => p.id === selectedPinId) ?? null;
  const sessionName = (conversationId: string) =>
    sessions?.find((s) => s.id === conversationId)?.display_name ?? null;

  return (
    <div className="flex min-h-0 flex-1">
      <div className="min-w-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-5 px-4 pt-4 pb-6 sm:px-6">
          <TabHeader
            title="Artifacts"
            description="Charts and tables shared from sessions. Anyone in the project can open their lineage and refresh them."
          />
          <div className="flex items-center gap-3 border-b border-border">
            <span className="-mb-px border-b-2 border-foreground px-0.5 py-2 text-sm font-medium">
              Shared
            </span>
            <div className="mb-1.5 ml-auto">
              <ViewToggle value={view} onChange={setView} />
            </div>
          </div>
          <PinnedArtifacts
            projectId={project.id}
            canCurate={isOwner}
            view={view}
            selectedPinId={selected?.id ?? null}
            onOpenLineage={(pinId) =>
              setSelectedPinId((current) => (current === pinId ? null : pinId))
            }
            sessionName={sessionName}
          />
        </div>
      </div>
      {selected && (
        <ResizableSidePanel role="complementary" aria-label="Artifact lineage">
          <div className="flex h-14 shrink-0 items-center gap-1 border-b border-border px-2">
            <span className="shrink-0 pl-1.5 text-sm font-medium text-muted-foreground">
              Artifacts
            </span>
            <ChevronRight className="size-4 shrink-0 text-muted-foreground/50" aria-hidden />
            <span className="min-w-0 truncate text-sm font-medium">{selected.display_name}</span>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSelectedPinId(null)}
              aria-label="Close lineage"
              className="ml-auto size-8 shrink-0"
            >
              <X className="size-4" />
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <PinnedArtifactWorkflow
              key={selected.id}
              pin={selected}
              summary={targetStep(selected)?.summary ?? null}
              artifactType={targetStep(selected)?.artifact_type ?? ""}
              onUnshare={isOwner ? () => void unpin(selected) : undefined}
              source={sessionName(selected.source_conversation_id) ?? "another member's session"}
            />
          </div>
        </ResizableSidePanel>
      )}
    </div>
  );
};
